import { Fragment, useState } from "react";
import { Check, Copy } from "lucide-react";

// Markdown des réponses de Vanta AI, rendu en éléments React (jamais de HTML
// injecté) : titres, listes, citations, tableaux, blocs de code avec copie,
// gras, italique, code, liens.

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

export function CopyButton({ text, label = "Copier", className = "" }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }).catch(() => {});
  };
  return (
    <button type="button" onClick={copy} className={`inline-flex items-center gap-1 ${className}`} aria-label={label} title={label}>
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span>{done ? "Copié" : label}</span>
    </button>
  );
}

// Coloration légère : commentaires, chaînes, nombres, mots-clés.
const KEYWORDS = new Set(("const let var function return if else for while do switch case break continue new class extends import from export default "
  + "async await try catch finally throw typeof instanceof in of true false null undefined def elif lambda pass with as yield None True False "
  + "and or not is print self public private protected static void int float double bool string struct fn mut use impl match local then end require").split(" "));
const TOKEN = /(\/\/[^\n]*|#[^\n]*|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b\d+(?:\.\d+)?\b|\b[A-Za-z_]\w*\b)/g;

function highlight(code, language) {
  const hashComments = /^(py|python|sh|bash|shell|yaml|yml|toml|rb|ruby|r|lua|dockerfile|ini|conf)$/i.test(language || "");
  const out = [];
  let last = 0;
  let i = 0;
  code.replace(TOKEN, (tok, _g, offset) => {
    if (offset > last) out.push(code.slice(last, offset));
    let cls = "";
    // « // » d'une adresse (https://…) ou d'un langage à commentaires « # » n'est pas un commentaire.
    const notComment = tok.startsWith("//") && (hashComments || code[offset - 1] === ":");
    if (notComment) { out.push(tok); last = offset + tok.length; return tok; }
    if (tok.startsWith("//") || tok.startsWith("/*") || (hashComments && tok.startsWith("#"))) cls = "text-white/40 italic";
    else if (tok.startsWith("#")) { out.push(tok); last = offset + tok.length; return tok; }
    else if (/^["'`]/.test(tok)) cls = "text-emerald-300";
    else if (/^\d/.test(tok)) cls = "text-amber-300";
    else if (KEYWORDS.has(tok)) cls = "text-violet-300";
    out.push(cls ? <span key={i++} className={cls}>{tok}</span> : tok);
    last = offset + tok.length;
    return tok;
  });
  if (last < code.length) out.push(code.slice(last));
  return out;
}

export function CodeBlock({ code, language }) {
  return (
    <div className="my-3 rounded-xl border border-white/10 bg-[#0b0b10] overflow-hidden" data-testid="vai-code">
      <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-white/45 border-b border-white/5 bg-white/[0.02]">
        <span className="font-mono">{language || "code"}</span>
        <CopyButton text={code} className="hover:text-white" />
      </div>
      <pre className="p-3 overflow-x-auto text-[13px] leading-relaxed font-mono text-white/85"><code>{highlight(code, language)}</code></pre>
    </div>
  );
}

function inline(text, keyBase = "i") {
  const out = [];
  const re = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*|__[^_\n]+__)|(~~[^~\n]+~~)|(\*[^*\s][^*\n]*\*|_[^_\s][^_\n]*_)|(\[[^\]\n]+\]\([^)\s]+\))|(https?:\/\/[^\s)<]+[^\s)<.,;:!?'"])/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${k++}`;
    const t = m[0];
    if (m[1]) out.push(<code key={key} className="px-1.5 py-0.5 rounded-md bg-white/10 text-[0.9em] font-mono text-pink-200">{t.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={key} className="font-semibold text-white">{inline(t.slice(2, -2), key)}</strong>);
    else if (m[3]) out.push(<del key={key}>{inline(t.slice(2, -2), key)}</del>);
    else if (m[4]) out.push(<em key={key}>{inline(t.slice(1, -1), key)}</em>);
    else if (m[5]) {
      const [, label, url] = t.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      out.push(SAFE_URL.test(url)
        ? <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="text-sky-300 underline underline-offset-2 hover:text-sky-200">{inline(label, key)}</a>
        : label);
    } else if (m[6]) out.push(<a key={key} href={t} target="_blank" rel="noopener noreferrer" className="text-sky-300 underline underline-offset-2 break-all">{t}</a>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function parseTable(lines) {
  const split = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const head = split(lines[0]);
  const rows = lines.slice(2).map(split);
  return { head, rows };
}

const isTableSep = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l || "");
const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;

function List({ items, ordered, keyBase }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`my-2 pl-6 space-y-1 ${ordered ? "list-decimal" : "list-disc"} marker:text-white/40`}>
      {items.map((it, i) => (
        <li key={`${keyBase}-${i}`} className="pl-1">
          {inline(it.text, `${keyBase}-${i}`)}
          {it.children.length > 0 && <List items={it.children} ordered={it.childOrdered} keyBase={`${keyBase}-${i}c`} />}
        </li>
      ))}
    </Tag>
  );
}

export default function Markdown({ text }) {
  const lines = (text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = line.match(/^\s*(```|~~~)\s*([\w+#.-]*)/);
    if (fence) {
      const body = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith(fence[1])) body.push(lines[i++]);
      i += 1;
      blocks.push(<CodeBlock key={key++} code={body.join("\n")} language={fence[2]} />);
      continue;
    }
    if (!line.trim()) { i += 1; continue; }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const cls = ["text-xl font-semibold mt-5 mb-2", "text-lg font-semibold mt-5 mb-2", "text-base font-semibold mt-4 mb-1.5"][Math.min(level, 3) - 1];
      const Tag = `h${Math.min(level + 1, 6)}`;
      blocks.push(<Tag key={key++} className={`${cls} text-white`}>{inline(h[2], `h${key}`)}</Tag>);
      i += 1;
      continue;
    }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { blocks.push(<hr key={key++} className="my-4 border-white/10" />); i += 1; continue; }
    if (line.trim().startsWith("|") && isTableSep(lines[i + 1])) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++]);
      const { head, rows: body } = parseTable(rows);
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04]"><tr>{head.map((c, j) => <th key={j} className="px-3 py-2 text-left font-semibold border-b border-white/10">{inline(c, `th${j}`)}</th>)}</tr></thead>
            <tbody>{body.map((r, ri) => <tr key={ri} className="border-b border-white/5 last:border-0">{r.map((c, j) => <td key={j} className="px-3 py-2 align-top">{inline(c, `td${ri}-${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (line.startsWith(">")) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith(">")) quote.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push(<blockquote key={key++} className="my-3 border-l-2 border-white/20 pl-4 text-white/70"><Markdown text={quote.join("\n")} /></blockquote>);
      continue;
    }
    if (LIST_ITEM.test(line)) {
      const ordered = /^\s*\d/.test(line);
      const items = [];
      while (i < lines.length && (LIST_ITEM.test(lines[i]) || (lines[i].startsWith("   ") && items.length && lines[i].trim()))) {
        const m = lines[i].match(LIST_ITEM);
        if (m && m[1].length >= 2 && items.length) {
          const parent = items[items.length - 1];
          parent.childOrdered = /^\d/.test(m[2]);
          parent.children.push({ text: m[3], children: [] });
        } else if (m) {
          items.push({ text: m[3], children: [] });
        } else {
          items[items.length - 1].text += ` ${lines[i].trim()}`;
        }
        i += 1;
      }
      blocks.push(<List key={key++} items={items} ordered={ordered} keyBase={`l${key}`} />);
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|>|\s*(```|~~~))/.test(lines[i]) && !LIST_ITEM.test(lines[i])
      && !(lines[i].trim().startsWith("|") && isTableSep(lines[i + 1]))) para.push(lines[i++]);
    const pk = key++;
    blocks.push(
      <p key={pk} className="my-2 leading-7">
        {para.map((p, j) => <Fragment key={j}>{j > 0 && <br />}{inline(p, `p${pk}-${j}`)}</Fragment>)}
      </p>,
    );
  }
  return <div className="vai-markdown text-[15px] text-white/90 break-words">{blocks}</div>;
}
